/**
 * Compresses a weekly_hours_allocated array change into a compact, date-labeled
 * summary that shows ONLY the weeks that actually changed (collapsing runs of
 * identical changes into a date range). The raw array is indexed by project
 * week, so index i maps to the project's start week + i weeks.
 *
 * This is computed at log time (the mutation site knows the project start date),
 * so each audit row is self-describing and historically accurate.
 */

import { addWeeks } from "@/utils/dateUtils";
import { getStartOfWeek } from "@/utils/weekUtils";

export interface FieldChange {
  old: unknown;
  new: unknown;
}

/** A weekly-hours change whose values are maps of week-date-range label → hours. */
export interface WeeklyHoursChange extends FieldChange {
  old: Record<string, number | null>;
  new: Record<string, number | null>;
}

export const WEEKLY_HOURS_FIELD = "weekly_hours_allocated";

function fmtDate(date: Date, withYear: boolean): string {
  // Week dates are computed in UTC (startOfWeek/addWeeks use UTC), so format in
  // UTC too — otherwise a behind-UTC locale shifts the label back a day.
  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

function rangeLabel(startWeek: Date, startIdx: number, endIdx: number): string {
  const start = addWeeks(startWeek, startIdx);
  const end = addWeeks(startWeek, endIdx);
  if (startIdx === endIdx) return fmtDate(start, true);
  return `${fmtDate(start, false)} – ${fmtDate(end, true)}`;
}

/**
 * Returns a {old,new} change whose values are objects keyed by week date range
 * (only changed weeks), or null if nothing changed / inputs aren't arrays.
 */
export function summarizeWeeklyHoursChange(
  oldArr: unknown,
  newArr: unknown,
  projectStartDate: string | null | undefined,
): WeeklyHoursChange | null {
  if (!Array.isArray(oldArr) || !Array.isArray(newArr) || !projectStartDate) {
    return null;
  }

  const startWeek = getStartOfWeek(new Date(projectStartDate));
  const len = Math.max(oldArr.length, newArr.length);
  const oldMap: Record<string, number | null> = {};
  const newMap: Record<string, number | null> = {};

  let i = 0;
  while (i < len) {
    const oldV = (oldArr[i] ?? null) as number | null;
    const newV = (newArr[i] ?? null) as number | null;
    if (oldV === newV) {
      i++;
      continue;
    }
    // Start of a changed run; extend while the (old,new) pair stays identical.
    const runOld = oldV;
    const runNew = newV;
    const startIdx = i;
    let endIdx = i;
    i++;
    while (i < len) {
      const o = (oldArr[i] ?? null) as number | null;
      const n = (newArr[i] ?? null) as number | null;
      if (o === runOld && n === runNew) {
        endIdx = i;
        i++;
      } else {
        break;
      }
    }
    const label = rangeLabel(startWeek, startIdx, endIdx);
    oldMap[label] = runOld;
    newMap[label] = runNew;
  }

  if (Object.keys(newMap).length === 0) return null;
  return { old: oldMap, new: newMap };
}

/**
 * If a changes object contains a weekly_hours_allocated array diff, replace it
 * with the compact date-labeled summary. Other fields pass through untouched.
 */
export function withWeeklyHoursSummary<T extends Record<string, FieldChange>>(
  changes: T,
  projectStartDate: string | null | undefined,
): Record<string, FieldChange> {
  const entry = changes[WEEKLY_HOURS_FIELD];
  if (!entry) return changes;
  const summary = summarizeWeeklyHoursChange(entry.old, entry.new, projectStartDate);
  const next: Record<string, FieldChange> = { ...changes };
  if (summary) {
    next[WEEKLY_HOURS_FIELD] = summary;
  } else {
    delete next[WEEKLY_HOURS_FIELD];
  }
  return next;
}
